import { PaddleBillingUsage } from '@easy-expense/data-schema-v2';
import { getTranslation } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import React from 'react';

import { AlertBanner } from './AlertBanner.component';
import { ManageSubscription } from './ManageSubscription.component';
import { PlatformSubscriptionNote } from './PlatformSubscriptionNote.component';
import { StatStrip } from './StatStrip.component';
import { TermToggle } from './TermToggle.component';
import { TEAMS_FAQ_URL, formatPrice } from '../../../lib/Billing/constants';
import {
  BillingTerm,
  getTeamPlanTitle,
  individualPlanFromPriceID,
  TeamPlanCode,
} from '../../../lib/Paddle';

export const SubscriptionTab: React.FC<{
  billingUsage: PaddleBillingUsage | null;
  currentPlan: TeamPlanCode | null;
  activeMembers: number;
  seatsAllowed: number;
  isOverSeats: boolean;
  term: BillingTerm;
  setTerm: (term: BillingTerm) => void;
  // Omitted for an individual (Starter/Professional) subscriber — those plans have a fixed
  // seat cap, not a self-serve add-seats purchase, so the "Add seats" row hides instead of
  // dead-ending on a no-op click. The overage banner itself still shows for them (falling back
  // to onChangePlan as its CTA) — a fixed seat cap doesn't make a real overage less real.
  onAddSeats?: () => void;
  // True while the subscription is scheduled to cancel — Paddle rejects seat
  // changes then, so the add-seats affordances are hidden/disabled rather than
  // dead-ending in an alert.
  addSeatsDisabled?: boolean;
  onCancel?: () => void;
  cancelDisabled?: boolean;
  // Provided only while the subscription is scheduled to cancel; reactivates it
  // in place instead of leaving the admin stranded for the lapsing window.
  onResume?: () => void;
  // Scrolls to planGrid below. Hidden for a legacy Teams Pro subscriber — there's
  // nowhere for them to go through this flow (see priceIDFor's Teams Pro carve-out).
  onChangePlan?: () => void;
  // The shared Starter/Professional/Team option-card row (see PlanGrid.component.tsx) —
  // built by the caller, which already holds the purchase/upgrade handlers and pricing
  // state this needs.
  planGrid?: React.ReactNode;
  planGridRef?: React.Ref<HTMLDivElement>;
  // True when the active subscription is annual — locks the term toggle so the
  // seat-change flow can't accidentally downgrade billing cycle.
  disableMonthlyTerm?: boolean;
  // True for a covered-but-non-owning member (specs/004-client-subscription-integration FR-003):
  // shows the same plan/seats/billing-date details, but hides every control that would let them
  // manage a subscription that isn't theirs — add seats, cancel, resume, upgrade.
  readOnly?: boolean;
  // Resolved, subscription-agnostic display values (from workspaceSubscriptionStatus) for a
  // read-only view whose coverage isn't Paddle's own billingUsage — a teammate's subscription, or
  // this viewer's own RevenueCat one. billingUsage/currentPlan stay Paddle-only and unused here
  // when these are provided.
  statDisplayOverride?: {
    planLabel: string;
    nextBillingLabel: string;
    perPeriodLabel: string;
    perPeriodAmount: string;
  };
}> = ({
  billingUsage,
  currentPlan,
  activeMembers,
  seatsAllowed,
  isOverSeats,
  term,
  setTerm,
  onAddSeats,
  addSeatsDisabled = false,
  onCancel,
  cancelDisabled,
  onResume,
  onChangePlan,
  planGrid,
  planGridRef,
  disableMonthlyTerm = false,
  readOnly = false,
  statDisplayOverride,
}) => {
  const resolvedIndividualPlan = individualPlanFromPriceID(billingUsage?.activePriceID);
  let individualPlanLabel: string;
  if (resolvedIndividualPlan === 'STARTER') {
    individualPlanLabel = getTranslation('Starter');
  } else if (resolvedIndividualPlan === 'PROFESSIONAL') {
    individualPlanLabel = getTranslation('Professional');
  } else {
    individualPlanLabel = billingUsage?.activeTier ?? getTranslation('Free');
  }
  const planLabel =
    statDisplayOverride?.planLabel ??
    (currentPlan ? getTeamPlanTitle(currentPlan) : individualPlanLabel);
  const nextBillingLabel =
    statDisplayOverride?.nextBillingLabel ?? billingUsage?.nextBillingDate ?? '—';
  // billingUsage.frequency comes back as 'month' | 'year' from Paddle —
  // translate the period label instead of concatenating the raw value.
  const isYearly = billingUsage?.frequency === 'year';
  const perPeriodAmount =
    statDisplayOverride?.perPeriodAmount ??
    billingUsage?.nextBillEstimate ??
    (isYearly
      ? `${formatPrice(0)} / ${getTranslation('year')}`
      : `${formatPrice(0)} / ${getTranslation('month')}`);
  const perPeriodLabel =
    statDisplayOverride?.perPeriodLabel ??
    (isYearly ? getTranslation('Per year') : getTranslation('Per month'));

  const seatsOver = Math.max(activeMembers - seatsAllowed, 0);

  // Legacy Teams Pro subscribers keep their plan untouched (manual outreach handles any
  // migration) and manage seats through the row above instead — the plan ladder advertises
  // plans they aren't on and can't self-serve into.
  const isLegacyProPlan = currentPlan === 'TEAMS_PRO';

  // A Team subscriber (or no subscription yet) can self-serve straight to "Add seats". An
  // individual (Starter/Professional) subscriber has a fixed seat cap instead — onAddSeats is
  // omitted for them (see the prop comment above), so the banner points at "Change plan"
  // (onChangePlan) instead of dead-ending. The overage itself is real either way, so the banner
  // shouldn't be suppressed just because the fix is "upgrade" instead of "add seats".
  let overSeatsCTA: { label: string; onPress: () => void } | null = null;
  let overSeatsDescription = getTranslation('Upgrade your plan to cover your full team.');
  if (onAddSeats) {
    overSeatsCTA = {
      label: `${getTranslation('Add')} ${seatsOver} ${getTranslation('seats')}`,
      onPress: onAddSeats,
    };
    overSeatsDescription = getTranslation('Add seats now to avoid interruptions.');
  } else if (onChangePlan) {
    overSeatsCTA = { label: getTranslation('Upgrade plan'), onPress: onChangePlan };
  }

  return (
    <Layout.Column>
      {isOverSeats && !addSeatsDisabled && !readOnly && overSeatsCTA ? (
        <>
          <AlertBanner
            title={`${getTranslation('Your workspace has')} ${activeMembers} ${getTranslation(
              'active users, but only',
            )} ${seatsAllowed} ${getTranslation('seats were purchased.')}`}
            description={overSeatsDescription}
            ctaLabel={overSeatsCTA.label}
            onPress={overSeatsCTA.onPress}
          />
          <Spacer.Vertical size={24} />
        </>
      ) : null}

      <StatStrip
        seatsUsed={activeMembers}
        seatsAllowed={seatsAllowed}
        planLabel={planLabel}
        nextBillingLabel={nextBillingLabel}
        perPeriodLabel={perPeriodLabel}
        perPeriodAmount={perPeriodAmount}
      />

      <Spacer.Vertical size={24} />

      <PlatformSubscriptionNote />

      {readOnly ? null : (
        <>
          <Spacer.Vertical size={32} />

          <ManageSubscription
            activeMembers={activeMembers}
            // addSeatsDisabled is true while scheduled to cancel -- Paddle rejects in-place
            // seat changes then, so the row is hidden here too, not just on the over-seats
            // banner above, matching the prop doc below.
            onAddSeats={addSeatsDisabled ? undefined : onAddSeats}
            onChangePlan={isLegacyProPlan ? undefined : onChangePlan}
            onCancel={onCancel}
            cancelDisabled={cancelDisabled}
            onResume={onResume}
          />
        </>
      )}

      {isLegacyProPlan || readOnly ? null : (
        <>
          <Spacer.Vertical size={32} />

          <div ref={planGridRef} />

          <Layout.Row align justify="space-between">
            <Layout.Column>
              <OpenSans.Primary weight="bold-700" size="m-18" color="primary">
                {getTranslation('Change plan')}
              </OpenSans.Primary>
              <Spacer.Vertical size={4} />
              <OpenSans.Primary size="xs-12" color="secondary">
                {getTranslation(
                  'Compare plans, add seats, or move to annual billing. Prorated charges apply immediately.',
                )}
              </OpenSans.Primary>
            </Layout.Column>
            <Layout.PressableRow
              center
              py={8}
              px={14}
              bg="white"
              radius={100}
              border={[1, 'solid', 'inputBorder']}
              onClick={() => window.open(TEAMS_FAQ_URL, '_blank')?.focus()}
            >
              <Icon name="help-circle-outline" size={14} color={theme.colors.brandPrimary} />
              <Spacer.Horizontal size={6} />
              <OpenSans.Primary size="xs-12">{getTranslation('Subscription FAQ')}</OpenSans.Primary>
            </Layout.PressableRow>
          </Layout.Row>

          <Spacer.Vertical size={16} />

          <Layout.Column>
            <TermToggle
              term={term}
              setTerm={setTerm}
              disableMonthly={disableMonthlyTerm}
              savingsLabel={getTranslation('Save up to 50%')}
            />
            {disableMonthlyTerm ? (
              <>
                <Spacer.Vertical size={6} />
                <OpenSans.Primary size="xs-12" color="secondary">
                  {getTranslation(
                    "You're on an annual plan. Contact support@easy-expense.com to switch billing cycles.",
                  )}
                </OpenSans.Primary>
              </>
            ) : null}
            <Spacer.Vertical size={16} />
            {planGrid}
          </Layout.Column>
        </>
      )}
    </Layout.Column>
  );
};
